import React from 'react';
import {
  Flex,
  Text,
  Switch,
  HStack,
  Tabs,
  TabList,
  Tab,
  useColorModeValue,
  Badge,
  Box,
  Divider,
  Icon,
  Tooltip,
  useBreakpointValue,
  VStack
} from '@chakra-ui/react';
import { Image, VideoCamera, Article, Cube, Tag, CirclesFour } from '@phosphor-icons/react';

export default function MediaGalleryHeader({ 
  mediaCount, 
  showThumbnailTags,
  onThumbnailTagsToggle,
  accentColor,
  onMediaTypeChange,
  currentMediaType = 'all'
}) {
  // Adventure Journal theme colors - using proper theme tokens
  const cardBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const dividerColor = useColorModeValue('#9C826B', '#6A4E33'); // border.default
  const secondaryText = useColorModeValue('#6B573B', '#B7A58B'); // text.muted
  const selectedTabBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const selectedTabColor = useColorModeValue('white', '#2C231B'); // High contrast
  const defaultTabColor = useColorModeValue('#3B2E1E', '#E6D7B8'); // text.default
  const badgeBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const badgeColor = useColorModeValue('white', '#2C231B'); // High contrast

  // Enhanced responsive adjustments for mobile with safety fallbacks
  const iconSize = useBreakpointValue({ base: 18, sm: 20, md: 22 }) || 20; // Smaller icons for better space usage
  const isCompact = useBreakpointValue({ base: true, sm: false }) || false;
  const switchSize = useBreakpointValue({ base: 'md', md: 'md' }) || 'md'; // Smaller switch for better space usage
  const tabSize = useBreakpointValue({ base: 'sm', md: 'md' }) || 'sm'; // Smaller tabs for better space usage
  const spacing = useBreakpointValue({ base: 3, md: 4 }) || 3; // Reduced spacing
  const padding = useBreakpointValue({ base: 4, md: 5 }) || 4; // Less padding for better space usage
  const fontSize = useBreakpointValue({ base: 'sm', md: 'md' }) || 'sm'; // Smaller text for better space usage
  const badgeSize = useBreakpointValue({ base: 'md', md: 'lg' }) || 'md'; // Smaller badges for better space usage
  
  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={dividerColor}
      p={padding}
      boxShadow="sm"
      position="relative"
    >
      <VStack spacing={spacing} align="stretch">
        {/* Top Controls Row */}
        <Flex 
          direction={{ base: 'column', sm: 'row' }}
          justify="space-between" 
          align={{ base: 'stretch', sm: 'center' }}
          gap={spacing}
        >
          {/* Feature Toggle - Enhanced for mobile */}
          <Tooltip 
            label="Show/hide feature tags on thumbnails" 
            hasArrow
            placement="top"
            fontSize="sm"
          >
            <HStack 
              spacing={3} 
              align="center" 
              minH="44px" // Ensure touch target size
              px={2}
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: useColorModeValue('#D9CAA7', '#483123') }}
            >
              <Icon 
                as={Tag} 
                color={selectedTabBg} 
                boxSize={iconSize} 
                weight="duotone" 
              />
              <Text 
                fontSize={fontSize}
                fontWeight="medium" 
                color={defaultTabColor}
                fontFamily="Inter"
                userSelect="none"
              >
                Show Features
              </Text>
              <Switch 
                size={switchSize}
                isChecked={showThumbnailTags}
                onChange={e => onThumbnailTagsToggle(e.target.checked)}
                sx={{
                  '.chakra-switch__track': {
                    bg: useColorModeValue('#9C826B', '#6A4E33'),
                    _checked: {
                      bg: selectedTabBg,
                    }
                  },
                  '.chakra-switch__thumb': {
                    bg: 'white',
                  }
                }}
              />
            </HStack>
          </Tooltip>

          {/* Media Count Badge */}
          <Badge
            bg={badgeBg}
            color={badgeColor}
            px={3}
            py={2}
            borderRadius="full"
            fontSize={fontSize}
            fontWeight="semibold"
            fontFamily="Inter"
            size={badgeSize}
            boxShadow="sm"
          >
            {mediaCount} {mediaCount === 1 ? 'item' : 'items'}
          </Badge>
        </Flex>

        {/* Media Type Tabs - Optimized for mobile */}
        <Box overflowX="auto" pb={1}>
          <Tabs
            variant="unstyled"
            size={tabSize}
            index={['all', 'image', 'video', '3d', 'article'].indexOf(currentMediaType)}
            onChange={(index) => {
              const types = ['all', 'image', 'video', '3d', 'article'];
              onMediaTypeChange(types[index]);
            }}
            isLazy
          >
            <TabList 
              spacing={2}
              minW="max-content"
              px={{ base: 0, sm: 0 }}
            >
              {[
                { type: 'all', icon: CirclesFour, label: 'All' },
                { type: 'image', icon: Image, label: 'Images' },
                { type: 'video', icon: VideoCamera, label: 'Videos' },
                { type: '3d', icon: Cube, label: '3D Models' },
                { type: 'article', icon: Article, label: 'Articles' }
              ].map(({ type, icon, label }) => (
                <Tab
                  key={type}
                  minH="36px" // Smaller touch target for better space usage
                  minW="36px" // Smaller square touch target
                  px={{ base: 2, md: 3 }}
                  py={{ base: 1, md: 1 }}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={currentMediaType === type ? selectedTabBg : dividerColor}
                  bg={currentMediaType === type ? selectedTabBg : 'transparent'}
                  color={currentMediaType === type ? selectedTabColor : defaultTabColor}
                  fontWeight={currentMediaType === type ? 'bold' : 'medium'}
                  fontSize={fontSize}
                  fontFamily="Inter"
                  _hover={{
                    bg: currentMediaType === type ? selectedTabBg : useColorModeValue('#D9CAA7', '#483123'),
                    transform: { base: 'scale(1.05)', md: 'translateY(-1px)' }, // Scale on mobile
                    boxShadow: 'md',
                    borderColor: selectedTabBg
                  }}
                  _active={{
                    transform: { base: 'scale(0.95)', md: 'translateY(0)' }
                  }}
                  transition="all 0.2s ease-in-out"
                  whiteSpace="nowrap"
                  onClick={() => onMediaTypeChange(type)}
                >
                  <HStack spacing={2}>
                    <Icon 
                      as={icon} 
                      boxSize={iconSize} 
                      weight="duotone"
                    />
                    <Text display={{ base: isCompact ? 'none' : 'block', sm: 'block' }}>
                      {label}
                    </Text>
                  </HStack>
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </Box>
      </VStack>
    </Box>
  );
}
